import { StrictMode, useEffect, useState, type ReactNode } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ClerkProvider, useAuth } from '@clerk/clerk-react';
import { LoadingState } from '@leanin/ui';

import { routeTree } from './routeTree.gen';
import { LocaleBoundary } from './i18n/LocaleBoundary';
import { detectBrowserLocale } from './i18n/detect';
import { useSession } from './lib/use-session';
import {
  ServerBodyProvider,
  isShowingServerBody,
  serverBodyRetry,
  useServerBodyDeadline,
  type Schedule,
  type ServerBodyEnvironment,
} from './lib/server-body-handoff';
import { NotFoundPage } from './components/shell/NotFoundPage';
import { MessagingDockProvider } from './components/messaging-dock';
import { PresenceSubscriptionsProvider } from './lib/presence-subscriptions';
import { setApiTokenGetter } from './lib/api-client';
import { shouldRetry } from './lib/query-retry';
import type { ReportError } from './lib/report-error';
import { setAnalyticsUserId } from './lib/analytics/data-layer';

// The app as main.tsx boots it, apart from the module side effects (Sentry,
// consent, analytics, stylesheets) that only the real entry point runs, so a
// test can boot the same tree with fake timers and a stubbed network.

export function createAppQueryClient(doc: Document): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        retry: serverBodyRetry(doc, shouldRetry),
      },
    },
  });
}

export function createAppRouter(
  queryClient: QueryClient,
  doc: Document,
  reportError: ReportError,
) {
  return createRouter({
    routeTree,
    defaultPreload: 'intent',
    context: { queryClient, member: null, reportError },
    // The app's scroll container is AppShell's <main> (overflow-y: auto), not
    // the window. Without these, navigating to a new page leaves <main> scrolled
    // wherever the previous page left it. While a server-composed body is on
    // screen the visitor is scrolling it, not the app, so the router's first
    // render must not reset the window under them; the handoff carries their
    // position into <main> instead.
    scrollRestoration: () => !isShowingServerBody(doc),
    scrollToTopSelectors: ['main'],
    // Branded 404 surface for any route that misses (manual URL typos, stale
    // bookmarks, dynamic routes that capture a non-matching segment like
    // `/profile/edit` -> `/profile/$id` with id='edit' -> notFound). Without
    // this the framework default is a bare `<p>Not Found</p>`.
    defaultNotFoundComponent: () => <NotFoundPage />,
  });
}

export type AppRouter = ReturnType<typeof createAppRouter>;

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter;
  }
}

// Router boot is gated on Clerk readiness, the standard Clerk SPA boot
// pattern. The router's initial navigation runs route guards whose session
// probe must carry `Authorization: Bearer <fresh token>`: Clerk's
// `__session` cookie is a ~60s JWT, stale for any returning visitor, so a
// probe that rides the cookie alone gets 401 and the guard bounces a
// genuinely signed-in user to /sign-in.
//
// Ordering guarantee: the same effect that registers the token getter
// flips `routerReady`, so by the time <RouterProvider> mounts (the render
// AFTER that effect committed) the api-client getter is in place, so the
// first session probe always carries the header.
//
// Bounded wait: if Clerk JS never loads (clerk.com blocked/unreachable),
// `isLoaded` stays false. After CLERK_LOAD_TIMEOUT_MS the router mounts
// anyway: the probe runs tokenless and the route guards surface their
// retryable-error / login states instead of a permanently stuck splash.
// Should Clerk finish loading later, the first effect registers the
// getter at that point.
//
// Local dev: `isLoaded` flips true for signed-out clients too (the SDK
// reports loaded once initialized, session or not), so the dev-bypass
// flow (dev_user_id cookie → X-Dev-User-Id header in authFetch) is never
// blocked by this gate. Must render under ClerkProvider.
export const CLERK_LOAD_TIMEOUT_MS = 8_000;

function ClerkRouterGate({
  router,
  schedule,
  reloadForStaleBundle,
}: {
  router: AppRouter;
  schedule: Schedule;
  reloadForStaleBundle: () => void;
}) {
  const { isLoaded, getToken } = useAuth();
  const [routerReady, setRouterReady] = useState(false);
  // A server-composed page's deadline runs from here, not from boot: the
  // gates before the router are bounded on their own.
  useServerBodyDeadline(routerReady);

  useEffect(() => {
    if (!isLoaded) return undefined;
    setApiTokenGetter(() => getToken());
    setRouterReady(true);
    return () => setApiTokenGetter(null);
  }, [isLoaded, getToken]);

  useEffect(() => {
    if (isLoaded) return undefined;
    return schedule(() => {
      console.warn(
        `Clerk did not load within ${CLERK_LOAD_TIMEOUT_MS}ms; mounting the router without a session token getter`,
      );
      setRouterReady(true);
    }, CLERK_LOAD_TIMEOUT_MS);
  }, [isLoaded, schedule]);

  if (!routerReady) {
    return <LoadingState placement="page" label="Loading" announceOn="mount" />;
  }
  return (
    <AppLocaleProvider onStaleBundle={reloadForStaleBundle}>
      <AnalyticsUserSync />
      {/* Above the router so the messaging dock's open/collapsed state and the
          tab's presence subscriptions survive every navigation, including the
          `_authed ↔ _public` Shell remount. */}
      <PresenceSubscriptionsProvider>
        <MessagingDockProvider>
          <RouterProvider router={router} />
        </MessagingDockProvider>
      </PresenceSubscriptionsProvider>
    </AppLocaleProvider>
  );
}

// Binds the GA4 user_id to the INTERNAL session user id (never Clerk's).
// Lives beside AppLocaleProvider, above BOTH route trees, so a signed-in
// member browsing `_public` pages is still identified, and sign-out (the
// session query resolving null) clears the id. Waits for the probe to
// settle so a loading state isn't reported as signed-out.
function AnalyticsUserSync() {
  const { data: ctx, isPending } = useSession();
  const userId = ctx?.session.userId ?? null;
  useEffect(() => {
    if (isPending) return;
    setAnalyticsUserId(userId);
  }, [isPending, userId]);
  return null;
}

// Single locale provider for the whole app. Uses the signed-in member's
// stored locale when a session exists (so it's consistent across BOTH the
// `_authed` and `_public` route trees: a logged-in member browsing a public
// page still sees their language), falling back to the browser-detected
// locale for anonymous visitors. Lives inside ClerkRouterGate so it reads the
// session only after the API token getter is wired, and wraps the router so
// navigating between route trees can't flip the language.
function AppLocaleProvider({
  onStaleBundle,
  children,
}: {
  onStaleBundle: () => void;
  children: ReactNode;
}) {
  const { data: ctx } = useSession();
  const locale = ctx?.member.locale ?? detectBrowserLocale();
  return (
    <LocaleBoundary locale={locale} onStaleBundle={onStaleBundle}>
      {children}
    </LocaleBoundary>
  );
}

// Hard cap on the first-paint font wait (main.tsx's `loadCriticalFonts`): a
// missing or genuinely slow font can't block the app forever. Past the cap
// we paint with the metric-matched fallback declared in globals.scss; the
// real font swaps in when it eventually arrives. Pattern matches Linear /
// Vercel dashboard / Notion's "block-then-bail" first-paint gating.
export const FONT_READY_TIMEOUT_MS = 1_200;

export interface AppBoot {
  readonly rootEl: HTMLElement;
  readonly router: AppRouter;
  readonly queryClient: QueryClient;
  readonly clerkPublishableKey: string;
  readonly schedule: Schedule;
  readonly loadCriticalFonts: () => Promise<void>;
  // Null when the document carries no server-composed body.
  readonly serverBody: ServerBodyEnvironment | null;
  // The guarded stale-bundle reload (stale-bundle.ts), for the locale
  // catalog loader.
  readonly reloadForStaleBundle: () => void;
}

// ClerkProvider is mounted unconditionally. The dev-bypass flag
// (LEANIN_ENV=local + DEV_AUTH_BYPASS=1) is a *backend* shortcut that
// honours an `X-Dev-User-Id` header; the frontend still owns the real
// sign-in / sign-up / 2FA flows, all of which call Clerk hooks. Skipping
// the provider in local-dev crashed those pages with "useUser /
// useSignIn can only be used within the <ClerkProvider />" the moment
// anyone navigated to /auth/* or /settings/sessions/*.
export async function bootApp(boot: AppBoot): Promise<Root> {
  await new Promise<void>((resolve) => {
    const cancel = boot.schedule(resolve, FONT_READY_TIMEOUT_MS);
    const paint = () => {
      cancel();
      resolve();
    };
    // A font that failed to load paints with the fallback at once, as one
    // past the cap does; it must not keep the app from rendering at all.
    boot.loadCriticalFonts().then(paint, (error: unknown) => {
      console.error('[fonts] first-paint font failed to load; painting with the fallback', {
        error,
      });
      paint();
    });
  });
  const root = createRoot(boot.rootEl);
  root.render(
    <StrictMode>
      <ServerBodyProvider value={boot.serverBody}>
        <ClerkProvider publishableKey={boot.clerkPublishableKey}>
          <QueryClientProvider client={boot.queryClient}>
            <ClerkRouterGate
              router={boot.router}
              schedule={boot.schedule}
              reloadForStaleBundle={boot.reloadForStaleBundle}
            />
          </QueryClientProvider>
        </ClerkProvider>
      </ServerBodyProvider>
    </StrictMode>,
  );
  return root;
}
